import { createSlice } from '@reduxjs/toolkit';
import Config from '../../config.js';

export const userSlice = createSlice({
    name: 'user',
    initialState: {
        token: '',
        providerDirectoryTokenData: {}
    },
    reducers: {
        setToken: (state, action) => {
            state.token = action.payload;
        },
        setProviderDirectoryTokenData: (state, action) => {
            state.providerDirectoryTokenData = action.payload;
        },
        loginIdentityServer: (state, action) => {
            fetch(Config.oAuth.identityServerUrl + '/connect/token', {
                method: 'POST',
                body: new URLSearchParams({
                    "grant_type": "password",
                    "client_id": Config.oAuth.clientId,
                    "username": action.payload.loginForm.username,
                    "password": action.payload.loginForm.password,
                    button: 'login',
                    RememberLogin: false,
                    scope: Config.oAuth.scopes?.join(' ')
                })
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error('Failed to obtain access token');
                    }
                    return response.json();
                })
                .then(tokenData => {
                    if (action.payload.callback)
                        action.payload.callback(tokenData);
                })
                .catch(error => {
                    if (action.payload.catch)
                        action.payload.catch(error);
                });
        },
        loginProviderDirectory: (state, action) => {
            fetch(Config.oAuth.identityServerUrl + '/connect/token', {
                method: 'POST',
                body: new URLSearchParams({
                    "grant_type": "client_credentials",
                    "client_id": Config.oAuth.providerDirectoryClientId,
                    "client_secret": Config.oAuth.providerDirectoryClientSecret,
                    button: 'login',
                    RememberLogin: false,
                    scope: Config.oAuth.scopes?.join(' ')
                })
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error('Failed to obtain access token for Provider Directory');
                    }
                    return response.json();
                })
                .then(tokenData => {
                    if (action.payload.callback)
                        action.payload.callback(tokenData);

                    return tokenData;
                })
                .catch(error => {
                    if (action.payload.catch)
                        action.payload.catch(error);
                });
        },
        register: (state, action) => {
            const url = `${Config.oAuth.identityServerUrl}/User/Register`;

            const formData = new FormData();
            formData.append('Email', action.payload.data.email);
            formData.append('ConfirmEmail', action.payload.data.confirmEmail);
            formData.append('UserName', action.payload.data.userName);
            formData.append('Password', action.payload.data.password);
            formData.append('ConfirmPassword', action.payload.data.confirmPassword);
            formData.append('returnUrl', Config.oAuth.redirectUri);
            formData.append('RoleSelected', Config.registrationRoleId);
            formData.append('returnJson', true);

            fetch(url, {
                method: 'POST',
                body: formData
            })
                .then(response => {
                    if (response.ok)
                        return response.json();

                    return response.json().then(errorData => {
                        throw new Error(JSON.stringify(errorData));
                    });
                })
                .then(response => {
                    if (action.payload.callback)
                        action.payload.callback(response);
                })
                .catch(error => {
                    // Log registration failure to the new API endpoint
                    const registrationLogData = {
                        Username: action.payload.data.userName,
                        Email: action.payload.data.email,
                        Success: false,
                        ErrorMessage: error instanceof TypeError ? 'Network error' : error.message,
                        IpAddress: 'unknown' // Will be determined by the server
                    };

                    // Call the new RegistrationLog API endpoint
                    fetch(`${Config.oAuth.identityServerUrl}/api/RegistrationLog`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(registrationLogData)
                    })
                    .then(logResponse => {
                        // Log response is optional, don't block the main error handling
                        if (!logResponse.ok) {
                            console.warn('Failed to log registration failure:', logResponse.statusText);
                        }
                    })
                    .catch(logError => {
                        // Don't let logging errors interfere with main error handling
                        console.warn('Error logging registration failure:', logError);
                    });

                    // Continue with original error handling
                    if (action.payload.catch) {
                        if (error instanceof TypeError)
                            return action.payload.catch(error);

                        return action.payload.validate(error.message);
                    }
                });
        },
        saveClaim: (state, action) => {
            const requestBody = new FormData();
            requestBody.append('UserId', action.payload.userId);

            let claims = [{ ClaimType: 'CG_ID', ClaimValue: action.payload.code }];

            claims.forEach((object, index) => {
                for (let key in object) {
                    requestBody.append(`Claims[${index}].${key}`, object[key]);
                }
            });

            //requestBody.append('__RequestVerificationToken', action.payload.antiForgeryToken);

            fetch(Config.oAuth.identityServerUrl + '/User/ManageClaims?userId=' + action.payload.userId, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': Config.csrfToken,
                },
                body: requestBody
            })
                .then(response => {
                    if (response.ok) {
                        if (action.payload.callback)
                            return action.payload.callback(response);
                    }
                    if (action.payload.catch)
                        action.payload.catch();
                })
                .catch(error => {
                    if (action.payload.catch)
                        action.payload.catch(error);
                });
        }
    }
})

export const { setToken, register, saveClaim, loginIdentityServer, loginProviderDirectory, setProviderDirectoryTokenData } = userSlice.actions

export default userSlice.reducer
